import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BarChart3, BellRing, CalendarDays as CalendarIcon, CheckCircle2, ClipboardCheck, Clock, Users } from "lucide-react";
import { cn } from "@v2/lib/utils";
import { Badge } from "@v2/components/ui/badge";
import { Button } from "@v2/components/ui/button";
import { Input } from "@v2/components/ui/input";
import { Label } from "@v2/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@v2/components/ui/select";
import { supabaseV2 } from "../../lib/supabase";
import { track } from "../../lib/analytics";
import { statusLabel } from "../../lib/labels";
import { currentIntlLocale } from "../../lib/i18n";
import { BrandHero, BrandMark } from "../../components/brand";
import { FullScreenState } from "../../components/state";
import type { ResponseStatus } from "../../types";

// Identificativo anonimo del browser, conservato in localStorage: finisce nel
// log delle risposte (session_response_events) per capire se lo stesso
// dispositivo risponde per piu' giocatori. Nessun dato personale.
function publicClientId(): string | null {
  try {
    const key = "eb_client_id";
    let id = localStorage.getItem(key);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(key, id);
    }
    return id;
  } catch {
    return null;
  }
}

export function PublicResponsePage({ token }: { token: string }) {
  // Magic link e' una landing brandata col gradient del team: deve restare
  // chiara anche se l'utente ha dark mode di sistema (altrimenti tile
  // bg-white + foreground bianco = illeggibili).
  const { t } = useTranslation("public");
  const [authedUserId, setAuthedUserId] = useState<string | null>(null);
  useEffect(() => {
    supabaseV2.auth.getSession().then(({ data }) => setAuthedUserId(data.session?.user.id || null));
  }, []);
  const [signupOpen, setSignupOpen] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupError, setSignupError] = useState<string | null>(null);
  const [signupOk, setSignupOk] = useState(false);
  const [su_first, setSuFirst] = useState("");
  const [su_last, setSuLast] = useState("");
  const [su_email, setSuEmail] = useState("");
  const [su_pwd, setSuPwd] = useState("");

  const [context, setContext] = useState<{
    session: {
      id: string;
      title: string;
      starts_at: string;
      location?: string | null;
      has_matches: boolean;
      response_deadline_at?: string | null;
    };
    team?: {
      id: string;
      name: string;
      slug: string;
      abbreviation?: string | null;
      primary_color?: string | null;
      secondary_color?: string | null;
    };
    totals?: { players: number; voters: number };
    players: Array<{
      id: string;
      display_name: string;
      role_code?: string | null;
      has_account?: boolean;
      status?: ResponseStatus | null;
    }>;
    matches?: Array<{
      id: string;
      opponent_name: string;
      home_away: string;
      starts_at?: string | null;
      competition_name?: string | null;
      our_score?: number | null;
      opponent_score?: number | null;
    }>;
  } | null>(null);
  const [selectedPlayerId, setSelectedPlayerId] = useState("");
  const [playerSearch] = useState("");
  const [status, setStatus] = useState<ResponseStatus | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    supabaseV2.rpc("get_session_response_context", { _token: token }).then(({ data, error }) => {
      if (error) {
        setLinkError(error.message.includes("Invalid or expired token") ? t("response.tokenInvalid") : t("response.loadError"));
        return;
      }
      setContext(data as any);
    });
  }, [token, t]);

  const submit = async (nextStatus: ResponseStatus) => {
    if (!selectedPlayerId) {
      setMessage(t("response.selectYourName"));
      return;
    }
    setLoading(true);
    setMessage(null);
    const { error } = await supabaseV2.rpc("submit_session_response_with_token", {
      _token: token,
      _player_id: selectedPlayerId,
      _status: nextStatus,
      _note: null,
      _client_id: publicClientId(),
    });
    setLoading(false);
    if (error) {
      // Scadenza passata mentre la pagina era aperta: il server rifiuta.
      if (error.message.includes("responses_closed")) {
        setServerClosed(true);
        return;
      }
      setMessage(error.message);
      return;
    }
    setStatus(nextStatus);
    setMessage(t("response.responseSaved"));
    // GA4 event "session_response" — lato player, anche senza account.
    if (context?.session.id) {
      track.sessionResponse({
        session_id: context.session.id,
        status: nextStatus as "present" | "late" | "absent",
        is_match_day: context.session.has_matches,
        team_id: context.team?.id || null,
      });
    }
    setContext((current) =>
      current
        ? {
            ...current,
            players: current.players.map((player) =>
              player.id === selectedPlayerId ? { ...player, status: nextStatus } : player
            ),
          }
        : current
    );
  };

  const filteredPlayers = (context?.players || []).filter((player) => {
    const needle = playerSearch.trim().toLowerCase();
    if (!needle) return true;
    return `${player.display_name} ${player.role_code || ""}`.toLowerCase().includes(needle);
  });
  const selectedPlayer = context?.players.find((player) => player.id === selectedPlayerId);

  const team = context?.team;
  const sessionTypeLabel = context?.session.has_matches
    ? t("response.sessionMatchDay")
    : t("response.sessionTraining");
  const teamGradient = team
    ? `linear-gradient(115deg, ${team.primary_color || "#005F7E"} 84%, ${team.secondary_color || "#004272"} 84%)`
    : "linear-gradient(115deg, #005F7E 84%, #004272 84%)";

  const deadline = context?.session.response_deadline_at;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!deadline) return;
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, [deadline]);
  const remainingMs = deadline ? new Date(deadline).getTime() - now : null;
  const [serverClosed, setServerClosed] = useState(false);
  const responsesClosed = serverClosed || (remainingMs != null && remainingMs <= 0);
  const remainingLabel = (() => {
    if (remainingMs == null) return null;
    if (remainingMs <= 0) return t("response.closed");
    const totalMin = Math.floor(remainingMs / 60_000);
    const days = Math.floor(totalMin / (60 * 24));
    const hours = Math.floor((totalMin % (60 * 24)) / 60);
    const minutes = totalMin % 60;
    if (days > 0) return `${days}g ${hours}h`;
    return `${hours}h ${minutes.toString().padStart(2, "0")}m`;
  })();

  const intlLocale = currentIntlLocale();
  const dateLabel = context?.session.starts_at
    ? new Date(context.session.starts_at).toLocaleDateString(intlLocale, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "—";
  const timeLabel = context?.session.starts_at
    ? new Date(context.session.starts_at).toLocaleTimeString(intlLocale, { hour: "2-digit", minute: "2-digit" })
    : "—";
  const deadlineLabel = deadline
    ? new Date(deadline).toLocaleString(intlLocale, {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  const totalPlayers = context?.totals?.players ?? context?.players.length ?? 0;
  const totalVoters = context?.totals?.voters ?? 0;
  const responded = !!status || !!selectedPlayer?.status;

  if (linkError) return <FullScreenState title={t("response.title")} description={linkError} />;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <BrandHero className="pb-5 pt-[max(1.25rem,env(safe-area-inset-top))] [&_.eb-logo]:h-9" />
      <div className="flex-1 p-3 sm:p-6">
      <div className="max-w-3xl mx-auto space-y-4 animate-fade-in">
        <div className="rounded-[20px] overflow-hidden" style={{ background: teamGradient }}>
          <div className="p-5 sm:p-6 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <div
                className="h-14 w-14 rounded-xl bg-[#f1f5f4] grid place-items-center font-bold text-base sm:text-lg shrink-0"
                style={{ color: team?.primary_color || "#005F7E" }}
              >
                {(team?.abbreviation || team?.name?.slice(0, 3) || "EB").toUpperCase().slice(0, 3)}
              </div>
              <div className="text-white min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold tracking-tight truncate">{team?.name || t("response.teamFallback")}</h1>
                <p className="text-white/80 text-sm truncate">{sessionTypeLabel}</p>
              </div>
            </div>
            {remainingLabel && (
              <div className="relative rounded-xl bg-white/10 backdrop-blur px-4 py-3 text-white text-center min-w-[150px] border border-white/15">
                <p className="font-mono uppercase tracking-[0.18em] text-[10px] font-semibold opacity-85">{t("response.timeRemaining")}</p>
                <p className="font-mono text-[28px] font-bold tabular-nums leading-none mt-1">{remainingLabel}</p>
                <p className="text-[10.5px] opacity-70 mt-1">{t("response.toRegister")}</p>
                <span className="absolute top-2 right-2 text-[9px] tracking-widest font-mono opacity-70">{t("response.live")}</span>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-2xl bg-card p-4 flex items-start gap-3">
            <Clock className="h-5 w-5 text-violet-500 mt-0.5" />
            <div className="min-w-0">
              <p className="font-semibold text-sm">{t("response.dateTime")}</p>
              <p className="text-sm text-muted-foreground capitalize">{dateLabel}</p>
              <p className="text-sm text-muted-foreground">{t("response.at", { time: timeLabel })}</p>
            </div>
          </div>
          <div className="rounded-2xl bg-card p-4 flex items-start gap-3">
            <Users className="h-5 w-5 text-violet-500 mt-0.5" />
            <div className="min-w-0">
              <p className="font-semibold text-sm">{t("response.participants")}</p>
              <p className="text-sm text-muted-foreground tabular-nums">
                {t("response.votersCount", { voters: totalVoters, total: totalPlayers })}
              </p>
            </div>
          </div>
          <div className="rounded-2xl bg-card p-4 flex items-start gap-3">
            <CalendarIcon className="h-5 w-5 text-amber-500 mt-0.5" />
            <div className="min-w-0">
              <p className="font-semibold text-sm">
                {responsesClosed
                  ? t("response.registrationsClosed")
                  : t("response.registrationsOpen")}
              </p>
              <p className="text-sm text-muted-foreground">{deadlineLabel ? t("response.until", { deadline: deadlineLabel }) : "—"}</p>
            </div>
          </div>
        </div>

        {/* CTA registrazione: appare SOPRA il box conferma SOLO dopo che
            l'utente ha votato, cosi' diventa la prima cosa visibile in
            risposta all'azione "ho appena confermato la presenza". */}
        {responded && !authedUserId && (
          <div className="rounded-[20px] overflow-hidden bg-card">
            {signupOk ? (
              <div className="p-5">
                <div className="rounded-lg px-3 py-2 text-sm bg-emerald-50 text-emerald-700 border border-emerald-200">
                  {t("response.accountCreatedOk")}
                </div>
              </div>
            ) : selectedPlayer?.has_account ? (
              <div className="p-5 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="font-bold text-base">{t("response.alreadyAccountTitle")}</p>
                  <p className="text-sm text-muted-foreground">{t("response.alreadyAccountDesc")}</p>
                </div>
                <Button variant="default" asChild className="shrink-0">
                  <a href="/">{t("response.loginCta")}</a>
                </Button>
              </div>
            ) : !signupOpen ? (
              <>
                <div className="p-5 sm:p-6 text-white" style={{ background: "hsl(var(--brand-navy))" }}>
                  <h2 className="text-lg sm:text-xl font-bold tracking-tight">{t("response.signupHero.title")}</h2>
                  <p className="text-sm text-white/85 mt-1 max-w-prose">{t("response.signupHero.subtitle")}</p>
                </div>
                <div className="p-5 sm:p-6 space-y-5">
                  {/* Stats EA del player se disponibili (RPC pubblica gated by token) */}
                  <EaPlayerStats token={token} playerId={selectedPlayerId} />

                  {/* 3 benefici concisi con icona */}
                  <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <BenefitTile
                      icon={<BarChart3 className="h-5 w-5" />}
                      iconBg="bg-emerald-50 text-emerald-600"
                      label={t("response.signupHero.benefit1")}
                    />
                    <BenefitTile
                      icon={<ClipboardCheck className="h-5 w-5" />}
                      iconBg="bg-violet-50 text-violet-600"
                      label={t("response.signupHero.benefit2")}
                    />
                    <BenefitTile
                      icon={<BellRing className="h-5 w-5" />}
                      iconBg="bg-amber-50 text-amber-600"
                      label={t("response.signupHero.benefit3")}
                    />
                  </ul>

                  <div className="flex flex-col sm:flex-row gap-2 pt-1">
                    {/* Accedi a SINISTRA (outline), Crea account a DESTRA (primary). */}
                    <Button variant="outline" size="lg" asChild className="flex-1">
                      <a href="/">{t("response.signupHero.ctaSecondary")}</a>
                    </Button>
                    <Button variant="default" size="lg" onClick={() => setSignupOpen(true)} className="flex-1">
                      {t("response.signupHero.ctaPrimary")}
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="p-5 sm:p-6">
                <form
                  className="space-y-2"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    setSignupLoading(true);
                    setSignupError(null);
                    const { error: signUpError } = await supabaseV2.auth.signUp({
                      email: su_email,
                      password: su_pwd,
                      options: {
                        data: { first_name: su_first, last_name: su_last },
                        emailRedirectTo: `${window.location.origin}/`,
                      },
                    });
                    if (signUpError) {
                      setSignupLoading(false);
                      setSignupError(signUpError.message);
                      return;
                    }
                    track.signUp({ source: "response_page" });
                    setSignupLoading(false);
                    setSignupOk(true);
                  }}
                >
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("response.createAccount")}</p>
                  <p className="text-[11px] text-muted-foreground -mt-1">{t("response.linkExplain")}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={su_first} onChange={(e) => setSuFirst(e.target.value)} placeholder={t("response.firstName")} required />
                    <Input value={su_last} onChange={(e) => setSuLast(e.target.value)} placeholder={t("response.lastName")} required />
                  </div>
                  <Input value={su_email} onChange={(e) => setSuEmail(e.target.value)} type="email" placeholder={t("response.email")} required />
                  <Input value={su_pwd} onChange={(e) => setSuPwd(e.target.value)} type="password" placeholder={t("response.passwordMin")} minLength={8} required />
                  {signupError && (
                    <div className="rounded-lg px-3 py-2 text-xs bg-rose-50 text-rose-700 border border-rose-200">{signupError}</div>
                  )}
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" className="flex-1" onClick={() => setSignupOpen(false)}>{t("response.cancel")}</Button>
                    <Button type="submit" variant="hero" className="flex-1" disabled={signupLoading}>{t("response.create")}</Button>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}

        <div className="rounded-[20px] bg-card overflow-hidden">
          <div className="p-5 text-white" style={{ background: "hsl(var(--brand-petrol))" }}>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              <h2 className="text-lg font-bold">{t("response.title")}</h2>
            </div>
            <p className="text-sm text-white/80 mt-1">
              {t("response.selectIntro", {
                kind: context?.session.has_matches ? t("response.kindEvent") : t("response.kindTraining"),
              })}
            </p>
          </div>
          <div className="p-5 space-y-4">
            <div className="space-y-2">
              <Label>{t("response.playerLabel")}</Label>
              <Select value={selectedPlayerId} onValueChange={setSelectedPlayerId}>
                <SelectTrigger>
                  <SelectValue placeholder={t("response.selectPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {filteredPlayers.map((player) => (
                    <SelectItem key={player.id} value={player.id}>
                      {player.display_name}
                      {player.status && player.status !== "no_response" ? t("response.alreadyResponded") : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedPlayer?.status && selectedPlayer.status !== "no_response" && (
              <Badge variant="secondary">{t("response.updateBadge")}</Badge>
            )}
            <div className="grid grid-cols-3 gap-2">
              {(["present", "late", "absent"] as ResponseStatus[]).map((nextStatus) => (
                <Button
                  key={nextStatus}
                  variant="outline"
                  disabled={loading || !selectedPlayerId || responsesClosed}
                  onClick={() => submit(nextStatus)}
                  className={cn("h-12", status === nextStatus && {
                    present: "bg-[hsl(var(--ok))] text-background hover:bg-[hsl(var(--ok))]",
                    late: "bg-[hsl(var(--warn))] text-background hover:bg-[hsl(var(--warn))]",
                    absent: "bg-[hsl(var(--danger))] text-background hover:bg-[hsl(var(--danger))]",
                    no_response: "",
                  }[nextStatus])}
                >
                  {statusLabel(nextStatus)}
                </Button>
              ))}
            </div>
            {responsesClosed && (
              <p className="rounded-lg px-3 py-2 text-sm bg-amber-50 text-amber-800 border border-amber-200">
                {t("response.closedContactCaptain")}
              </p>
            )}
            {!selectedPlayerId && !responded && !responsesClosed && (
              <p className="text-center text-sm text-muted-foreground rounded-lg bg-muted/40 py-2">
                {t("response.selectNameToContinue")}
              </p>
            )}
            {message && (
              <div
                className={cn(
                  "rounded-lg px-3 py-2 text-sm",
                  status
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : "bg-rose-50 text-rose-700 border border-rose-200"
                )}
              >
                {message}
              </div>
            )}
          </div>
        </div>

        {/* Footer minimalista: 'powered by' + logo (che contiene il wordmark),
            tutto cliccabile e linkato alla landing. L'ordine "powered by [logo]"
            e' corretto grammaticalmente: powered by ElevenBase. */}
        <a
          href="https://elevenbase.pro"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 pt-4 pb-2 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
          aria-label="powered by ElevenBase"
        >
          <span>{t("response.poweredBy")}</span>
          <BrandMark size="sm" />
        </a>
      </div>
      </div>
    </div>
  );
}

// =====================================================================
// Sub-components
// =====================================================================

function BenefitTile({
  icon, iconBg, label,
}: {
  icon: React.ReactNode;
  iconBg: string;
  label: string;
}) {
  return (
    <li className="flex flex-col items-center text-center gap-2 p-3 rounded-lg border bg-white">
      <span className={cn("h-10 w-10 rounded-full grid place-items-center shrink-0", iconBg)}>
        {icon}
      </span>
      <p className="text-sm font-semibold leading-tight">{label}</p>
    </li>
  );
}

type EaStats = {
  ea_persona: string | null;
  ea_name: string | null;
  games_played: number | null;
  goals: number | null;
  assists: number | null;
  avg_rating: number | null;
  mom: number | null;
  win_pct: number | null;
};

function EaPlayerStats({ token, playerId }: { token: string; playerId: string }) {
  const { t } = useTranslation("public");
  const [stats, setStats] = useState<EaStats | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!playerId) { setStats(null); return; }
    (async () => {
      try {
        const { data } = await supabaseV2.rpc("get_player_ea_stats_with_token", {
          _token: token,
          _player_id: playerId,
        });
        if (!cancelled) setStats((data as EaStats | null) ?? null);
      } catch {
        if (!cancelled) setStats(null);
      }
    })();
    return () => { cancelled = true; };
  }, [token, playerId]);

  if (!stats) return null;
  // Per essere "utili", servono almeno games_played > 0
  if (!stats.games_played || stats.games_played <= 0) return null;

  const fmtRating = (v: number | null) =>
    v == null ? "—" : (Math.round(v * 10) / 10).toFixed(1);

  return (
    <div className="rounded-lg border bg-gradient-to-br from-slate-50 to-white p-4">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
        {t("response.signupHero.eaStats.label")}{stats.ea_name ? ` · ${stats.ea_name}` : ""}
      </p>
      <div className="grid grid-cols-3 gap-2">
        <EaKpi label={t("response.signupHero.eaStats.games")} value={String(stats.games_played)} />
        <EaKpi label={t("response.signupHero.eaStats.goals")} value={String(stats.goals ?? 0)} />
        <EaKpi label={t("response.signupHero.eaStats.rating")} value={fmtRating(stats.avg_rating)} />
      </div>
    </div>
  );
}

function EaKpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-white border p-2 text-center">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
      <p className="text-xl font-bold tabular-nums leading-tight">{value}</p>
    </div>
  );
}
